import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import { type RefObject, useEffect, useState } from 'react';

import { type DraggedResource, resourceOf } from './dnd';

export type CardDropZone = 'left' | 'right' | 'combine';
type Anchor = { after_id: string } | { before_id: string };

/** Share of a card's width on each side that means "beside it"; the middle means "group with it". */
const SIDE_SHARE = 0.25;

interface Options {
  ref: RefObject<HTMLElement | null>;
  enabled: boolean;
  kind: DraggedResource['kind'];
  id: string;
  workspaceId: string;
  data: Record<string, unknown>;
  previousId: string | undefined;
  nextId: string | undefined;
  /** A card of the same kind was dropped beside this one. */
  onDropBeside: ((sourceId: string, anchor: Anchor) => void) | undefined;
  /** A card (of any kind) was dropped ON this one: group the two. */
  onCombine?: ((source: DraggedResource) => void) | undefined;
}

/**
 * Dragging of a catalog card. The whole card is the handle. Over another card of the same kind the
 * sides reorder and the middle groups; a card of another kind can only be grouped (the two kinds
 * have separate manual orders). Native drag and drop: touch scrolling is never intercepted, and on
 * a touch device the same actions are in the card's menu. Alt + arrow on the focused card is the
 * keyboard alternative.
 */
export function useCardDrag({
  ref,
  enabled,
  kind,
  id,
  workspaceId,
  data,
  previousId,
  nextId,
  onDropBeside,
  onCombine,
}: Options) {
  const [dragging, setDragging] = useState(false);
  const [zone, setZone] = useState<CardDropZone | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;
    const zoneOf = (selfData: Record<string | symbol, unknown>): CardDropZone | null =>
      selfData.combine === true
        ? 'combine'
        : (extractClosestEdge(selfData) as 'left' | 'right' | null);
    // keyboard reordering: Alt + arrow moves the focused card beside its neighbour
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey) return;
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowUp') && previousId) {
        event.preventDefault();
        onDropBeside?.(id, { before_id: previousId });
      } else if ((event.key === 'ArrowRight' || event.key === 'ArrowDown') && nextId) {
        event.preventDefault();
        onDropBeside?.(id, { after_id: nextId });
      }
    };
    element.addEventListener('keydown', onKeyDown);
    const stopKeys = () => {
      element.removeEventListener('keydown', onKeyDown);
    };
    return combine(
      stopKeys,
      draggable({
        element,
        getInitialData: () => data,
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          const dragged = resourceOf(source.data);
          if (!dragged || dragged.workspaceId !== workspaceId) return false;
          if (dragged.kind === kind && dragged.id === id) return false;
          return dragged.kind === kind || onCombine !== undefined;
        },
        getData: ({ input, source }) => {
          const edged = attachClosestEdge(data, {
            element,
            input,
            allowedEdges: ['left', 'right'],
          });
          if (!onCombine) return edged;
          const rect = element.getBoundingClientRect();
          const share = (input.clientX - rect.left) / Math.max(rect.width, 1);
          const middle = share > SIDE_SHARE && share < 1 - SIDE_SHARE;
          const sameKind = resourceOf(source.data)?.kind === kind;
          return { ...edged, combine: middle || !sameKind };
        },
        onDrag: ({ self }) => {
          setZone(zoneOf(self.data));
        },
        onDragLeave: () => {
          setZone(null);
        },
        onDrop: ({ self, source }) => {
          setZone(null);
          const dragged = resourceOf(source.data);
          if (!dragged) return;
          const where = zoneOf(self.data);
          if (where === 'combine') {
            onCombine?.(dragged);
            return;
          }
          // Dropped where it already is: nothing to save.
          if (where === 'left' && previousId !== dragged.id) {
            onDropBeside?.(dragged.id, { before_id: id });
          }
          if (where === 'right' && nextId !== dragged.id) {
            onDropBeside?.(dragged.id, { after_id: id });
          }
        },
      }),
    );
    // `data` is rebuilt from the same ids on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, id, kind, workspaceId, onDropBeside, onCombine, previousId, nextId, ref]);

  return { dragging, zone };
}
